import { useState } from "react";
import { stampReasonWords } from "@/lib/locationStamps";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { Calendar, ClipboardList, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ReservationTimesEditor } from "./ReservationTimesEditor";
import { useTurnoverTaskModal } from "@/contexts/TurnoverTaskModalContext";
import { useCleaningTasks } from "@/hooks/useCleaningTasks";
import { useDeleteReservation } from "@/hooks/useReservations";
import { toast } from "@/hooks/use-toast";
import { usePropertyWithAccess } from "@/hooks/useProperties";
import { usePropertyAssignableMembers, USER_PREFIX } from "@/hooks/usePropertyAssignableMembers";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { getTaskAssigneeLabel, displayNameFromProfile, formatPersonLabel } from "@/lib/taskAssigneeLabel";
import { canDeleteReservations, type PropertyAccessLevel, type PropertyUserRole } from "@/lib/propertyTabs";
import { resolveTeamPermissions } from "@/lib/teamPermissions";
import { supabase } from "@/integrations/supabase/client";
import type { CleaningTaskWithRelations } from "@/types/cleaning";

type ReservationPayload = Record<string, unknown> & {
  id: string;
  start_date: string;
  end_date: string;
  source?: string | null;
  summary?: string | null;
  check_in_time?: string | null;
  check_out_time?: string | null;
  reservation_type?: string;
};

interface UnifiedCalendarDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "reservation" | "task";
  reservation: ReservationPayload | null;
  task: CleaningTaskWithRelations | null;
  propertyId: string | null;
  propertyName: string | null;
  onOpenInPropertySchedule: (propertyId: string, focus: { focusReservationId?: string; focusTaskId?: string }) => void;
  onTaskCreated?: () => void;
  onSwitchToTask?: (task: CleaningTaskWithRelations) => void;
  /** Called after reservation is removed (drawer should close and clear selection). */
  onReservationRemoved?: () => void;
}

export function UnifiedCalendarDrawer({
  open,
  onOpenChange,
  mode,
  reservation,
  task,
  propertyId,
  propertyName,
  onOpenInPropertySchedule,
  onTaskCreated,
  onSwitchToTask,
  onReservationRemoved,
}: UnifiedCalendarDrawerProps) {
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const { openTurnoverTaskModal } = useTurnoverTaskModal();
  const { data: cleaningTasks } = useCleaningTasks(propertyId ?? undefined);
  const deleteReservation = useDeleteReservation(propertyId ?? undefined);
  const { data: property } = usePropertyWithAccess(propertyId ?? undefined);
  const { user, profile } = useAuth();
  const { members: assignableMembers } = usePropertyAssignableMembers(propertyId ?? undefined);
  const propertyCtx = usePropertyContext();

  const { data: drawerAccess } = useQuery({
    queryKey: ["my-property-access-level", propertyId, user?.id],
    queryFn: async () => {
      if (!propertyId || !user?.id) return null;
      const { data, error } = await supabase.rpc("get_my_property_access_level", {
        p_property_id: propertyId,
      });
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return row as { access_level: string; is_owner: boolean; permissions?: unknown };
    },
    enabled:
      !!propertyId &&
      !!user?.id &&
      profile?.role !== "cleaner" &&
      !(propertyCtx?.propertyId === propertyId && propertyCtx.role != null),
  });

  const canRemoveReservation = useMemo(() => {
    if (propertyCtx?.propertyId === propertyId && propertyCtx.role != null) {
      return (
        propertyCtx.isOwner ||
        canDeleteReservations(
          propertyCtx.role,
          propertyCtx.accessLevel,
          propertyCtx.permissions
        )
      );
    }
    if (!drawerAccess) return false;
    if (drawerAccess.is_owner) return true;
    const accessLevel: PropertyAccessLevel | null =
      drawerAccess.access_level === "full" || drawerAccess.access_level === "limited"
        ? drawerAccess.access_level
        : null;
    const role: PropertyUserRole = "cohost";
    const permissions = resolveTeamPermissions(drawerAccess.permissions ?? null, accessLevel);
    return canDeleteReservations(role, accessLevel, permissions);
  }, [propertyCtx, propertyId, drawerAccess]);

  const assigneeLabelMaps = useMemo(() => {
    const assigneeUserIdToName = new Map<string, string>();
    const ownerId = (property as { owner_id?: string } | null)?.owner_id;
    if (ownerId && user?.id === ownerId) {
      assigneeUserIdToName.set(ownerId, displayNameFromProfile(profile as { full_name?: string | null; email?: string | null } | null, "Owner"));
    }
    for (const member of assignableMembers ?? []) {
      if (!member?.value) continue;
      if (member.value.startsWith(USER_PREFIX) && member.userId) {
        assigneeUserIdToName.set(member.userId, (member.name?.trim() || member.email?.split("@")[0] || "Team Member"));
      }
    }
    const safeMembers = (assignableMembers ?? []).filter((x): x is NonNullable<typeof x> => x != null && x.value != null);
    return {
      getCleanerName: (cleanerId: string | null) => {
        if (!cleanerId) return null;
        const m = safeMembers.find((x) => x.cleanerId === cleanerId || x.value === cleanerId);
        const raw = m?.name?.trim() ?? null;
        return raw ? formatPersonLabel(raw) || raw : null;
      },
      getAssigneeUserName: (userId: string | null) => (userId ? assigneeUserIdToName.get(userId) ?? null : null),
    };
  }, [assignableMembers, property, user?.id, profile]);

  const hasTask =
    reservation &&
    (cleaningTasks ?? []).some((t) => t.reservation_id === (reservation as { id?: string }).id);

  // Use latest task from query so assignee shows correctly after editing
  const currentTask =
    mode === "task" && task && (cleaningTasks ?? []).length > 0
      ? (cleaningTasks ?? []).find((t) => t.id === task.id) ?? task
      : task;

  const assigneeLabel =
    currentTask && propertyId
      ? getTaskAssigneeLabel(currentTask as { assigned_type?: string; cleaner_id?: string | null; assignee_user_id?: string | null }, assigneeLabelMaps)
      : null;

  const handleRemoveFromCalendar = async () => {
    if (!reservation?.id || !propertyId) return;
    if (!canRemoveReservation) {
      toast({
        title: "Only Full Access can remove reservations",
        description: "Ask the account owner if this reservation needs to be removed.",
        variant: "destructive",
      });
      return;
    }
    try {
      await deleteReservation.mutateAsync(reservation.id);
      toast({ title: "Removed", description: "Reservation removed. It won’t come back on Sync." });
      setRemoveConfirmOpen(false);
      onReservationRemoved?.();
      onOpenChange(false);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Failed to remove reservation";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="flex flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {mode === "reservation" ? <Calendar className="h-5 w-5" /> : <ClipboardList className="h-5 w-5" />}
              {mode === "reservation" ? "Reservation" : "Task"}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex flex-1 flex-col gap-4 overflow-y-auto">
            {propertyName && (
              <p className="text-sm text-muted-foreground truncate">{propertyName}</p>
            )}

            {mode === "reservation" && reservation && propertyId && (
              <>
                <div>
                  <p className="font-medium text-foreground">
                    {format(new Date(reservation.start_date), "MMM d, yyyy")} –{" "}
                    {format(new Date(reservation.end_date), "MMM d, yyyy")}
                  </p>
                  {reservation.source && (
                    <p className="text-sm text-muted-foreground">{String(reservation.source)}</p>
                  )}
                </div>
                {reservation.reservation_type !== "blocked" && (
                  <>
                    <ReservationTimesEditor
                      reservationId={reservation.id}
                      propertyId={propertyId}
                      checkInTime={(reservation.check_in_time as string) ?? null}
                      checkOutTime={(reservation.check_out_time as string) ?? null}
                    />
                    {!hasTask && reservation && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4">
                        <p className="text-sm font-medium text-amber-800">No cleaning task for this stay.</p>
                        <Button
                          className="mt-3 w-full sm:w-auto"
                          onClick={() => {
                            openTurnoverTaskModal({
                              mode: "create",
                              propertyId: propertyId!,
                              reservationId: reservation.id,
                            });
                            onOpenChange(false);
                          }}
                        >
                          Create Task
                        </Button>
                      </div>
                    )}
                  </>
                )}
                {canRemoveReservation ? (
                  <div className="mt-auto pt-4 border-t border-border">
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setRemoveConfirmOpen(true)}
                      disabled={deleteReservation.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove from calendar
                    </Button>
                  </div>
                ) : null}
              </>
            )}

            {mode === "task" && currentTask && propertyId && (
              <>
                <div>
                  <p className="font-medium text-foreground">{currentTask.title || (currentTask as { notes?: string }).notes || "Task"}</p>
                  <p className="text-sm text-muted-foreground">
                    {(currentTask.checklists as { name?: string } | null)?.name ?? "No checklist"} ·{" "}
                    {assigneeLabel ?? "Unassigned"}
                  </p>
                </div>
                {/* On-site start verification (owner/team visibility) */}
                {currentTask.started_at && (
                  <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                    <p className="font-medium text-foreground">
                      Started on-site:{" "}
                      {(currentTask as { start_location_verified?: boolean | null }).start_location_verified === true
                        ? "Yes"
                        : (currentTask as { start_location_verified?: boolean | null }).start_location_verified === false
                          ? "No"
                          : "—"}
                    </p>
                    {typeof (currentTask as { start_location_distance_m?: number | null }).start_location_distance_m === "number" && (
                      <p className="text-muted-foreground mt-0.5">
                        Distance at start:{" "}
                        {Math.round((currentTask as { start_location_distance_m?: number }).start_location_distance_m!)} m
                        {typeof (currentTask as { start_location_accuracy_m?: number | null }).start_location_accuracy_m === "number" && (
                          <> · Accuracy: ±{Math.round((currentTask as { start_location_accuracy_m: number }).start_location_accuracy_m)} m</>
                        )}
                      </p>
                    )}
                    {(currentTask as { start_location_override_reason?: string | null }).start_location_override_reason && (
                      <p className="text-amber-700 dark:text-amber-400 mt-1">
                        {(() => {
                          const reason = (currentTask as { start_location_override_reason: string }).start_location_override_reason;
                          return stampReasonWords(reason) ?? `Override: ${reason}`;
                        })()}
                      </p>
                    )}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    openTurnoverTaskModal({
                      mode: "edit",
                      propertyId: propertyId!,
                      taskId: currentTask.id,
                    });
                    onOpenChange(false);
                  }}
                >
                  Edit date, time, assignee, checklist
                </Button>
              </>
            )}

          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={removeConfirmOpen} onOpenChange={setRemoveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this reservation?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the reservation and its open turnover from your schedule. Sync will not bring it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFromCalendar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteReservation.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
